import { buildAPIUrl } from "../../api/apiHelpers"; // Utility function to build the API URL with query parameters
import HttpClient from "../../api/httpClient";
import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import {
  Collection,
  CreateCollection,
  UpdateCollection,
} from "../../Models/Collection";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { PaginatedResponse } from "../../Models/Pagination";
import { Shipment } from "../../Models/Shipment";

export class CollectionsService {
  private httpClient: HttpClient; // Instance of the HttpInterface to make HTTP requests

  constructor() {
    this.httpClient = new HttpClient(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all collections from the collection service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of collections.
   */
  all(
    params: FetchParam = defaultFetchParam
  ): Promise<PaginatedResponse<Collection>> {
    return this.httpClient.fetch(
      buildAPIUrl(`${API_BASE_URL}/shipment-service/collections`, params),
      { method: "GET" }
    );
  }

  /**
   * Fetches a specific collection by its ID from the API.
   *
   * @param id The unique identifier of the collection to fetch.
   * @returns A promise that resolves with the API response containing the collection details.
   */
  get(id: string): Promise<Collection> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${id}`,
      { method: "GET" }
    );
  }

  /**
   * Creates a new collection in the collection service API.
   *
   * @param collection The details of the collection to create.
   * @returns A promise that resolves with the API response confirming the creation of the collection.
   */
  create(collection: CreateCollection): Promise<Collection> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections`,
      {
        method: "POST", // HTTP method for the request
        body: JSON.stringify(collection), // Sends the collection data as JSON in the request body
      }
    );
  }

  /**
   * Updates an existing collection in the collection service API.
   *
   * @param collection The updated details of the collection.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(collection: UpdateCollection): Promise<Collection> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${collection.id}`,
      {
        method: "PUT", // HTTP method for the request
        body: JSON.stringify(collection), // Sends the updated collection data as JSON in the request body
      }
    );
  }

  /**
   * Deletes a collection from the collection service API.
   *
   * @param id The unique identifier of the collection to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<void> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${id}`,
      {
        method: "DELETE", // HTTP method for the request
      }
    );
  }

  /**
   * Books a collection from the collection service API.
   *
   * @param id The unique identifier of the collection to book.
   * @returns A promise that resolves with the API response confirming the booking.
   */
  book(id: string): Promise<void> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${id}/book`,
      {
        method: "GET", // HTTP method for the request
      }
    );
  }

  /**
   * Creates a relation between a specific collection and a shipment in the API.
   *
   * @param id The unique identifier of the collection.
   * @param shipmentId The unique identifier of the shipment to associate.
   * @returns A promise that resolves when the relation has been successfully created.
   */
  createShipmentRelation(id: string, shipmentId: string): Promise<void> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${id}/shipments/${shipmentId}`,
      {
        method: "PUT", // HTTP method for the request
      }
    );
  }

  /**
   * Fetches all shipments associated with a specific collection.
   *
   * @param id The unique identifier of the collection.
   * @returns A promise that resolves with an array of related shipments.
   */
  getRelatedShipments(id: string): Promise<Shipment[]> {
    return this.httpClient.fetch(
      `${API_BASE_URL}/shipment-service/collections/${id}/shipments`,
      {
        method: "GET", // HTTP method for the request
      }
    );
  }
}
